import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Box, Card, Chip, Stack, Typography } from '@mui/material';
import {
  AssignmentRounded,
  CheckCircleRounded,
  ChevronRightRounded,
  HourglassEmptyRounded,
  PriorityHighRounded,
  ScheduleRounded,
  WarningAmberRounded,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useSectionAssignments } from '../hooks/use-portal-data';
import { PortalEmpty, PortalLoading } from '../PortalShell';
import { formatDate, formatDateTime } from '../../../lib/i18n-format';
import { usePortalViewing } from '../../../stores/portal-view.store';

/** Relative due chip color/text — mirrors DashboardPage helper but local to this file */
function relativeDue(
  t: TFunction<'sis'>,
  iso?: string,
): {
  label: string;
  color: 'error' | 'warning' | 'success' | 'default';
} {
  if (!iso) return { label: t('portal.assignments.noDate'), color: 'default' };
  const now = new Date();
  const due = new Date(iso);
  const diffMs = due.getTime() - now.getTime();
  const diffHrs = diffMs / (1000 * 60 * 60);
  const diffDays = diffHrs / 24;
  if (diffHrs < 0) {
    const overdueDays = Math.floor(-diffDays);
    return {
      label:
        overdueDays === 0
          ? t('portal.assignments.dueToday')
          : t('portal.assignments.overdueDays', { count: overdueDays }),
      color: 'error',
    };
  }
  if (diffHrs < 24)
    return {
      label: t('portal.assignments.dueInHours', { count: Math.round(diffHrs) }),
      color: 'error',
    };
  if (diffDays < 7)
    return {
      label: t('portal.assignments.dueInDays', { count: Math.ceil(diffDays) }),
      color: 'warning',
    };
  return {
    label: t('portal.assignments.dueOn', {
      date: formatDate(due, { day: 'numeric', month: 'short' }),
    }),
    color: 'default',
  };
}

function dueLabel(iso?: string): string {
  if (!iso) return '';
  return formatDateTime(iso, {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function CourseAssignmentsPage(): JSX.Element {
  const { t } = useTranslation('sis');
  const { sectionId } = useParams<{ sectionId: string }>();
  const navigate = useNavigate();
  // «Ver portal»: el detalle de una tarea no es de la vista.
  const viewing = usePortalViewing();
  const { data, isLoading } = useSectionAssignments(sectionId);

  const items = useMemo(() => (data?.items ?? []).filter((a) => a.publishedAt), [data]);

  const stats = useMemo(() => {
    let pending = 0;
    let overdue = 0;
    let submitted = 0;
    let graded = 0;
    const now = Date.now();
    for (const a of items) {
      const s = a.studentSubmission?.status;
      if (s === 'graded' || s === 'returned') graded++;
      else if (s === 'submitted') submitted++;
      else {
        pending++;
        if (a.dueAt && new Date(a.dueAt).getTime() < now) overdue++;
      }
    }
    return { pending, overdue, submitted, graded };
  }, [items]);

  if (isLoading) return <PortalLoading />;

  if (items.length === 0) {
    return (
      <PortalEmpty
        icon={<AssignmentRounded fontSize="inherit" />}
        title={t('portal.assignments.emptyTitle')}
        body={t('portal.assignments.emptyBody')}
      />
    );
  }

  // Group: pendientes (sorted by due asc) / entregadas / calificadas
  const pending = items
    .filter((a) => {
      const s = a.studentSubmission?.status;
      return !s || s === 'draft';
    })
    .sort((a, b) => (a.dueAt ?? '').localeCompare(b.dueAt ?? ''));
  const submittedList = items
    .filter((a) => a.studentSubmission?.status === 'submitted')
    .sort((a, b) =>
      (b.studentSubmission?.submittedAt ?? '').localeCompare(
        a.studentSubmission?.submittedAt ?? '',
      ),
    );
  const gradedList = items
    .filter(
      (a) => a.studentSubmission?.status === 'graded' || a.studentSubmission?.status === 'returned',
    )
    .sort((a, b) =>
      (b.studentSubmission?.submittedAt ?? '').localeCompare(
        a.studentSubmission?.submittedAt ?? '',
      ),
    );

  const renderGroup = (
    title: string,
    list: typeof items,
    accent: string,
    icon: JSX.Element,
  ): JSX.Element | null => {
    if (list.length === 0) return null;
    return (
      <Box>
        <Stack direction="row" alignItems="center" spacing={1} mb={1}>
          {icon}
          <Typography variant="overline" color="#8b949e" fontWeight={700}>
            {title}
          </Typography>
          <Chip
            size="small"
            label={list.length}
            sx={{ bgcolor: accent, color: '#fff', height: 18, fontSize: 11, fontWeight: 700 }}
          />
        </Stack>
        <Card sx={{ bgcolor: '#161b22', border: '1px solid #21262d', borderRadius: 2 }}>
          {list.map((a, i) => {
            const sub = a.studentSubmission;
            const isGraded = sub?.status === 'graded' || sub?.status === 'returned';
            const isSubmitted = sub?.status === 'submitted' || isGraded;
            const dueInfo = relativeDue(t, a.dueAt);
            const submittedLate =
              isSubmitted && sub?.submittedAt && a.dueAt && sub.submittedAt > a.dueAt;
            const isClosed =
              !isSubmitted && !!a.lockAt && new Date(a.lockAt).getTime() < Date.now();
            return (
              <Box
                key={a.id}
                onClick={() =>
                  !viewing && navigate(`/portal-v2/courses/${sectionId}/assignments/${a.id}`)
                }
                sx={{
                  px: 2.5,
                  py: 1.75,
                  borderBottom: i === list.length - 1 ? 'none' : '1px solid #21262d',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  cursor: viewing ? 'default' : 'pointer',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.03)' },
                }}
              >
                <Box sx={{ flexShrink: 0 }}>
                  {isGraded ? (
                    <CheckCircleRounded sx={{ color: '#10b981' }} fontSize="small" />
                  ) : isSubmitted ? (
                    <HourglassEmptyRounded sx={{ color: '#3b82f6' }} fontSize="small" />
                  ) : dueInfo.color === 'error' ? (
                    <WarningAmberRounded sx={{ color: '#f87171' }} fontSize="small" />
                  ) : (
                    <AssignmentRounded sx={{ color: '#8b949e' }} fontSize="small" />
                  )}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
                    <Typography variant="body2" fontWeight={600} color="#e6edf3" noWrap>
                      {a.name}
                    </Typography>
                    {submittedLate && (
                      <Chip
                        size="small"
                        label={t('portal.assignments.submittedLate')}
                        sx={{
                          height: 18,
                          fontSize: 10,
                          fontWeight: 700,
                          bgcolor: 'rgba(245,158,11,0.18)',
                          color: '#fbbf24',
                        }}
                      />
                    )}
                    {isClosed && (
                      <Chip
                        size="small"
                        label={t('portal.assignments.closed')}
                        sx={{
                          height: 18,
                          fontSize: 10,
                          fontWeight: 700,
                          bgcolor: 'rgba(248,113,113,0.18)',
                          color: '#f87171',
                        }}
                      />
                    )}
                  </Stack>
                  <Stack
                    direction="row"
                    spacing={1.25}
                    alignItems="center"
                    flexWrap="wrap"
                    useFlexGap
                    sx={{ mt: 0.25 }}
                  >
                    {!isSubmitted && a.dueAt && (
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 600,
                          color:
                            dueInfo.color === 'error'
                              ? '#f87171'
                              : dueInfo.color === 'warning'
                                ? '#fbbf24'
                                : '#8b949e',
                        }}
                      >
                        {dueInfo.label}
                      </Typography>
                    )}
                    {isSubmitted && sub?.submittedAt && (
                      <Typography variant="caption" color="#8b949e">
                        {t('portal.assignments.submittedOn', {
                          date: dueLabel(sub.submittedAt),
                        })}
                      </Typography>
                    )}
                    <Typography variant="caption" color="#6e7681">
                      {t('portal.assignments.pointsWeight', {
                        points: a.pointsPossible,
                        weight: a.weight,
                      })}
                    </Typography>
                  </Stack>
                </Box>
                {sub?.finalPoints !== undefined && (
                  <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                    <Typography variant="body2" fontWeight={700} color="#10b981">
                      {sub.finalPoints.toFixed(1)} / {a.pointsPossible}
                    </Typography>
                    {sub.latePenalty !== undefined && sub.latePenalty > 0 && (
                      <Typography variant="caption" color="#fbbf24" display="block">
                        {t('portal.assignments.latePenalty', {
                          percent: (sub.latePenalty * 100).toFixed(0),
                        })}
                      </Typography>
                    )}
                  </Box>
                )}
                <ChevronRightRounded sx={{ color: '#6e7681', flexShrink: 0 }} fontSize="small" />
              </Box>
            );
          })}
        </Card>
      </Box>
    );
  };

  return (
    <Stack spacing={3}>
      {/* Hero: status snapshot */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
          gap: 1.5,
        }}
      >
        <StatTile
          label={t('portal.assignments.statPending')}
          value={stats.pending}
          accent="#f59e0b"
          hot
          icon={<AssignmentRounded sx={{ fontSize: 18 }} />}
        />
        <StatTile
          label={t('portal.assignments.statOverdue')}
          value={stats.overdue}
          accent="#ef4444"
          hot
          icon={<PriorityHighRounded sx={{ fontSize: 18 }} />}
        />
        <StatTile
          label={t('portal.assignments.statInReview')}
          value={stats.submitted}
          accent="#3b82f6"
          icon={<HourglassEmptyRounded sx={{ fontSize: 18 }} />}
        />
        <StatTile
          label={t('portal.assignments.statGraded')}
          value={stats.graded}
          accent="#10b981"
          icon={<CheckCircleRounded sx={{ fontSize: 18 }} />}
        />
      </Box>

      {renderGroup(
        t('portal.assignments.groupPending'),
        pending,
        '#f59e0b',
        <ScheduleRounded sx={{ color: '#f59e0b', fontSize: 18 }} />,
      )}
      {renderGroup(
        t('portal.assignments.groupInReview'),
        submittedList,
        '#3b82f6',
        <HourglassEmptyRounded sx={{ color: '#3b82f6', fontSize: 18 }} />,
      )}
      {renderGroup(
        t('portal.assignments.groupGraded'),
        gradedList,
        '#10b981',
        <CheckCircleRounded sx={{ color: '#10b981', fontSize: 18 }} />,
      )}
    </Stack>
  );
}

function StatTile({
  label,
  value,
  accent,
  icon,
  hot = false,
}: {
  label: string;
  value: number;
  accent: string;
  icon: JSX.Element;
  hot?: boolean;
}): JSX.Element {
  const isHot = value > 0 && hot;
  return (
    <Card
      sx={{
        bgcolor: isHot ? `${accent}1a` : '#161b22',
        border: `1px solid ${isHot ? accent : '#21262d'}`,
        borderRadius: 2,
        p: 1.5,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1}>
        <Box sx={{ color: accent }}>{icon}</Box>
        <Typography variant="caption" color="#8b949e" fontWeight={700} sx={{ letterSpacing: 0.5 }}>
          {label.toUpperCase()}
        </Typography>
      </Stack>
      <Typography variant="h4" fontWeight={700} color={value > 0 ? accent : '#6e7681'} mt={0.5}>
        {value}
      </Typography>
    </Card>
  );
}
